import {
  useCallback, useState,
} from 'react';
import { CONFIG } from '~/_shared/constants/config';
import {
  generateCodeChallenge, generateCodeVerifier, generateSecureString,
} from '~/_shared/utils/crypto';
import {
  GOOGLE_SECURITY_TOKEN_ITEM_KEY, GOOGLE_SECURITY_TOKEN_KEY, GOOGLE_SSO_PROMPT, GOOGLE_SSO_SCOPES,
} from './googleSsoPage.container';
import {
  MS_PKCE_CODE_VERIFIER_KEY,
  MS_SECURITY_TOKEN_ITEM_KEY, MS_SECURITY_TOKEN_KEY,
  MS_SSO_SCOPES,
} from './msSsoPage.container';
import {
  getGoogleSsoRedirectUri, getMsSsoRedirectUri,
} from './sso.helpers';

export const useSsoLogin = () => {
  const [isGeneratingCodeChallenge, setIsGeneratingCodeChallenge] = useState(false);

  const onGoogleLogin = useCallback(() => {
    const clientId = CONFIG.GOOGLE_OAUTH_CLIENT_ID;
    const redirectUri = getGoogleSsoRedirectUri();
    const scope = GOOGLE_SSO_SCOPES;
    const secureString = generateSecureString();
    sessionStorage.setItem(GOOGLE_SECURITY_TOKEN_ITEM_KEY, secureString);
    const state = encodeURIComponent(JSON.stringify({ [GOOGLE_SECURITY_TOKEN_KEY]: secureString }));

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope,
      state,
      access_type: 'offline',
      prompt: GOOGLE_SSO_PROMPT,
    }).toString()}`;

    window.location.href = authUrl;
  }, []);

  const onMicrosoftLogin = useCallback(async () => {
    const clientId = CONFIG.MS_OAUTH_CLIENT_ID || '';
    const redirectUri = getMsSsoRedirectUri();
    const secureString = generateSecureString();
    sessionStorage.setItem(MS_SECURITY_TOKEN_ITEM_KEY, secureString);
    const state = encodeURIComponent(JSON.stringify({ [MS_SECURITY_TOKEN_KEY]: secureString }));

    // PKCE implementation
    const codeVerifier = generateCodeVerifier();

    setIsGeneratingCodeChallenge(true);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    setIsGeneratingCodeChallenge(false);

    // Store code verifier in sessionStorage to use during token exchange
    sessionStorage.setItem(MS_PKCE_CODE_VERIFIER_KEY, codeVerifier);

    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${new URLSearchParams({
      tenant: 'common',
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: MS_SSO_SCOPES,
      state,
      prompt: 'consent',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256', // SHA-256 hashing method
    }).toString()}`;

    window.location.href = authUrl;
  }, []);

  return {
    isLoading: isGeneratingCodeChallenge,
    onGoogleLogin,
    onMicrosoftLogin,
  };
};
